import {
  type SyntheticEvent, useCallback, useEffect, useState,
} from "react";
import type { CampaignMedia } from "@/shared/types/featureTeasing.types";

/** Without a `playing` event after this delay, autoplay is considered blocked. */
export const AUTOPLAY_STALL_MS = 3000;

export const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

type Options = {
  mediaKind: CampaignMedia["kind"]
  endScreenAtMs: number | null
  enabled: boolean
};

type VideoEvent = SyntheticEvent<HTMLVideoElement>;

export type VideoRevealHandlers = {
  onPlaying: () => void
  onTimeUpdate: (event: VideoEvent) => void
  onSeeked: (event: VideoEvent) => void
  onPause: () => void
  onEnded: () => void
};

/**
 * Activation CTAs wait for the video's end screen. Reveal is latched: once true it
 * never goes back, so a media refresh or a backward seek cannot hide the CTAs.
 */
export const useEndScreenReveal = ({ mediaKind, endScreenAtMs, enabled }: Options) => {
  const [revealed, setRevealed] = useState(() =>
    !enabled || endScreenAtMs === null || mediaKind === "image" || prefersReducedMotion());
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (revealed || playing) return;
    const timer = window.setTimeout(() => setRevealed(true), AUTOPLAY_STALL_MS);
    return () => window.clearTimeout(timer);
  }, [revealed, playing]);

  const reveal = useCallback(() => setRevealed(true), []);
  const check = useCallback((event: VideoEvent) => {
    if (endScreenAtMs !== null && event.currentTarget.currentTime * 1000 >= endScreenAtMs) setRevealed(true);
  }, [endScreenAtMs]);
  const onPlaying = useCallback(() => setPlaying(true), []);

  const videoHandlers: VideoRevealHandlers = {
    onPlaying,
    onTimeUpdate: check,
    onSeeked: check,
    onPause: reveal,
    onEnded: reveal,
  };

  return { revealed, reveal, videoHandlers };
};
